import React, { useEffect, useState, useCallback } from "react";
import AppShell from "../components/AppShell";
import Filters from "../components/dashboard/Filters";
import KpiCard from "../components/dashboard/KpiCard";
import {
  ActiveSubsChart,
  NewSubsChart,
  RevenueChart,
} from "../components/dashboard/TimeSeriesChart";
import PlatformBreakdownTable from "../components/dashboard/PlatformBreakdownTable";
import Phase2ComingSoon from "../components/dashboard/Phase2ComingSoon";
import ComparisonBanner from "../components/dashboard/ComparisonBanner";
import api from "../lib/api";
import { intFmt, pct, usd } from "../lib/format";

export default function Dashboard() {
  const [preset, setPreset] = useState("30d");
  const [customRange, setCustomRange] = useState({ start: null, end: null });
  const [platform, setPlatform] = useState("all");
  const [compareTo, setCompareTo] = useState("prior");
  const [kpis, setKpis] = useState(null);
  const [series, setSeries] = useState([]);
  const [breakdown, setBreakdown] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = { preset, platform, compare_to: compareTo };
      const breakdownParams = { preset };
      if (preset === "custom" && customRange.start && customRange.end) {
        params.start = customRange.start;
        params.end = customRange.end;
        breakdownParams.start = customRange.start;
        breakdownParams.end = customRange.end;
      }
      const [k, t, b] = await Promise.all([
        api.get("/metrics/kpis", { params }),
        api.get("/metrics/timeseries", { params }),
        api.get("/metrics/breakdown", { params: breakdownParams }),
      ]);
      setKpis(k.data);
      setSeries(t.data);
      setBreakdown(b.data);
    } finally {
      setLoading(false);
    }
  }, [preset, customRange, platform, compareTo]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleCustomRange = (start, end) => {
    setCustomRange({ start, end });
    setPreset("custom");
  };

  const compareLabel = compareTo === "yoy" ? "vs last year" : "vs prior period";

  return (
    <AppShell>
      <div className="space-y-6" data-testid="dashboard-root">
        <div className="flex flex-col gap-2">
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Overview</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight">
            MyID+ subscription performance
          </h1>
        </div>

        <Filters
          preset={preset}
          onPresetChange={setPreset}
          customStart={customRange.start}
          customEnd={customRange.end}
          onCustomRangeChange={handleCustomRange}
          platform={platform}
          onPlatformChange={setPlatform}
          compareTo={compareTo}
          onCompareToChange={setCompareTo}
        />

        <ComparisonBanner meta={kpis?.meta} loading={loading} />

        <div className="kpi-grid">
          <KpiCard
            testId="kpi-new-subs"
            label="New MyID+ subs"
            value={kpis?.new_subscriptions?.value}
            changePct={kpis?.new_subscriptions?.change_pct}
            format={intFmt}
            loading={loading}
            formula={kpis?.new_subscriptions?.formula}
            sources={kpis?.new_subscriptions?.sources}
            note={kpis?.new_subscriptions?.note}
            compareLabel={compareLabel}
          />
          <KpiCard
            testId="kpi-active-subs"
            label="Active subscribers"
            value={kpis?.active_subscribers?.value}
            changePct={kpis?.active_subscribers?.change_pct}
            format={intFmt}
            loading={loading}
            formula={kpis?.active_subscribers?.formula}
            sources={kpis?.active_subscribers?.sources}
            note={kpis?.active_subscribers?.note}
            compareLabel={compareLabel}
          />
          <KpiCard
            testId="kpi-revenue"
            label="Revenue"
            value={kpis?.revenue_usd?.value}
            changePct={kpis?.revenue_usd?.change_pct}
            format={usd}
            loading={loading}
            formula={kpis?.revenue_usd?.formula}
            sources={kpis?.revenue_usd?.sources}
            note={kpis?.revenue_usd?.note}
            compareLabel={compareLabel}
          />
          <KpiCard
            testId="kpi-net-new"
            label="Net new subs"
            value={kpis?.net_new_subscribers?.value}
            changePct={kpis?.net_new_subscribers?.change_pct}
            format={intFmt}
            loading={loading}
            formula={kpis?.net_new_subscribers?.formula}
            sources={kpis?.net_new_subscribers?.sources}
            note={kpis?.net_new_subscribers?.note}
            compareLabel={compareLabel}
          />
          <KpiCard
            testId="kpi-renewal"
            label="Renewal rate"
            value={kpis?.renewal_rate?.value}
            changePct={kpis?.renewal_rate?.change_pct}
            format={pct}
            loading={loading}
            formula={kpis?.renewal_rate?.formula}
            sources={kpis?.renewal_rate?.sources}
            note={kpis?.renewal_rate?.note}
            compareLabel={compareLabel}
          />
          <KpiCard
            testId="kpi-churn"
            label="Churn rate"
            value={kpis?.churn_rate?.value}
            changePct={kpis?.churn_rate?.change_pct}
            format={pct}
            invertChange
            loading={loading}
            formula={kpis?.churn_rate?.formula}
            sources={kpis?.churn_rate?.sources}
            note={kpis?.churn_rate?.note}
            compareLabel={compareLabel}
          />
          <KpiCard
            testId="kpi-conversion"
            label="Trial → paid"
            value={kpis?.conversion_rate?.value}
            changePct={kpis?.conversion_rate?.change_pct}
            format={pct}
            loading={loading}
            formula={kpis?.conversion_rate?.formula}
            sources={kpis?.conversion_rate?.sources}
            note={kpis?.conversion_rate?.note}
            compareLabel={compareLabel}
          />
          <KpiCard
            testId="kpi-active-start"
            label="Active subscribers (start)"
            value={kpis?.active_subscribers?.prev_value}
            changePct={null}
            format={intFmt}
            loading={loading}
            formula="Same rule as Active subscribers, evaluated at the start of the selected window. Used as the denominator for churn rate."
            sources={kpis?.active_subscribers?.sources}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          <NewSubsChart data={series} loading={loading} />
          <ActiveSubsChart data={series} loading={loading} />
        </div>
        <RevenueChart data={series} loading={loading} />

        <PlatformBreakdownTable rows={breakdown} loading={loading} />

        <Phase2ComingSoon />
      </div>
    </AppShell>
  );
}
